/**
 * ============================================
 * DATE RANGE FILTER
 * ============================================
 *
 * Simple control bound to the shared DateRangeContext: a start date, an end
 * date, and a Reset. No presets — pick a start and/or end (either bound may be
 * left open) and Reset clears back to all-time.
 *
 * Two layouts via `orientation`:
 *   - 'horizontal' (default): one row — used inline in the PS5 / Friend Code
 *     headers.
 *   - 'vertical': stacked, full-width inputs — used in the Analytics floating
 *     card where it sits in a narrow left-side column.
 *
 * Filters only the date-keyed time-series charts (and, on Analytics, the per-day
 * additive rollups). All-time aggregate cards are unaffected.
 */

import { Calendar, RotateCcw } from 'lucide-react';
import { useTheme } from '@/context/ThemeContext';
import { useDateRange } from '@/context/DateRangeContext';

interface Props {
  className?: string;
  orientation?: 'horizontal' | 'vertical';
}

export function DateRangeFilter({ className = '', orientation = 'horizontal' }: Props) {
  const { isDarkMode } = useTheme();
  const { start, end, setCustom, setPreset, isAllTime } = useDateRange();
  const vertical = orientation === 'vertical';

  // In the vertical (Analytics floating card) layout, match the dashboard's
  // other controls (time-scale select, Export/Refresh buttons): rounded-lg,
  // px-3 py-2, text-sm. The inline horizontal layout (PS5 / Friend Code) stays
  // compact (text-xs) to fit its header row.
  const sizeCls = vertical
    ? 'w-full rounded-lg px-3 py-2 text-sm font-medium'
    : 'rounded-md px-2 py-1 text-xs';

  const inputCls = `${sizeCls} focus:outline-none focus:ring-2 focus:ring-blue-500 ${
    isDarkMode
      ? 'bg-gray-800 border border-gray-700 text-gray-100'
      : 'bg-white border border-gray-300 text-gray-900'
  }`;

  const sepCls = `${vertical ? 'text-sm text-center' : 'text-xs'} ${
    isDarkMode ? 'text-gray-500' : 'text-gray-400'
  }`;

  const startInput = (
    <input
      type="date"
      value={start ?? ''}
      max={end ?? undefined}
      onChange={(e) => setCustom(e.target.value || null, end)}
      className={inputCls}
      aria-label="Start date"
    />
  );

  const endInput = (
    <input
      type="date"
      value={end ?? ''}
      min={start ?? undefined}
      onChange={(e) => setCustom(start, e.target.value || null)}
      className={inputCls}
      aria-label="End date"
    />
  );

  const resetBtn = (
    <button
      onClick={() => setPreset('all')}
      disabled={isAllTime}
      title="Reset to all time"
      className={`inline-flex items-center justify-center gap-1.5 font-medium border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
        vertical ? 'w-full rounded-lg px-3 py-2 text-sm' : 'rounded-md px-2.5 py-1 text-xs'
      } ${
        isAllTime
          ? isDarkMode
            ? 'bg-gray-800 border-gray-700 text-gray-600 cursor-not-allowed'
            : 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
          : isDarkMode
            ? 'bg-gray-800 border-gray-700 text-gray-200 hover:bg-gray-700'
            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'
      }`}
    >
      <RotateCcw className={vertical ? 'h-4 w-4' : 'h-3 w-3'} />
      Reset
    </button>
  );

  if (vertical) {
    return (
      <div className={`flex flex-col gap-1.5 ${className}`}>
        {startInput}
        <span className={sepCls}>to</span>
        {endInput}
        {resetBtn}
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 flex-wrap ${className}`}>
      <Calendar className={`h-4 w-4 flex-shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
      {startInput}
      <span className={sepCls}>to</span>
      {endInput}
      {resetBtn}
    </div>
  );
}
